import {
	Component,
	EventEmitter,
	Input,
	Output
}                                from '@angular/core';
import { BaseComponent }         from '@core/bases/base-component/base.component';
import { VerticalAlignmentEnum } from '@models/common.model';
import {
	FilterListType,
	FilterValueType,
	IActiveFilterData,
	IFilter
}                                from '@shared/list-operations/model/filter.model';
import {
	ListOperationsIdentifierEnum,
	ListOperationsResetEnum,
	ListOperationsResetModeEnum,
	ListOperationsResetType
}                                from '@shared/list-operations/model/list-operations.model';
import { ListOperationsService } from '@shared/list-operations/services/list-operations.service';
import { Subscription }          from 'rxjs';
import { takeUntil }             from 'rxjs/operators';

@Component( {
	selector: 'app-list-filter[listOperationIdentifier]',
	templateUrl: './list-filter.component.html',
	styleUrls: [ './list-filter.component.scss' ]
} )
export class ListFilterComponent extends BaseComponent {

	private _defaultFilterData: IFilter;
	private _id: number;
	private _listOperationIdentifier: ListOperationsIdentifierEnum;
	private _resetListOperationsSubscription: Subscription;

	listFilterValue: FilterValueType[];
	readonly verticalAlignmentEnum = VerticalAlignmentEnum;

	get defaultFilterData() {
		return this._defaultFilterData;
	}

	@Input() set defaultFilterData( defaultFilterData: IFilter ) {
		this.initialize( defaultFilterData );
	}

	get listOperationIdentifier() {
		return this._listOperationIdentifier;
	}

	@Input() set listOperationIdentifier( listOperationIdentifier: ListOperationsIdentifierEnum ) {
		if ( !listOperationIdentifier ) { return; }

		this._listOperationIdentifier = listOperationIdentifier;

		this._resetListOperationsSubscription?.unsubscribe();

		this._resetListOperationsSubscription =
			this.listOperationsService.select.resetListOperationsData(
				listOperationIdentifier,
				'ListFilterComponent:set listOperationIdentifier'
			)
			.pipe(
				takeUntil( this.ngUnsubscribe )
			)
			.subscribe( ( resetData: ListOperationsResetType ) => {
				if (
					resetData?.listOperator === ListOperationsResetEnum.ALL ||
					resetData?.listOperator === ListOperationsResetEnum.FILTERS_ALL ||
					(
						resetData?.listOperator === ListOperationsResetEnum.FILTER &&
						resetData?.identifier === this._id
					)
				) {

					switch ( resetData.resetMode ) {
						case ListOperationsResetModeEnum.CLEAR:
							this.clearControlValue();
							break;

						case ListOperationsResetModeEnum.DEFAULT:
						case ListOperationsResetModeEnum.RESTORE:
							this.resetFilter( resetData.resetMode );
							break;
					}
				}
			} );
	}

	// TODO temporary flag prop to distinguish between old and new, will be removed when old has been completely replaced by new
	@Input() sidebar: boolean;

	@Output() filterItemChange: EventEmitter<IActiveFilterData> = new EventEmitter();


	constructor(
		private readonly listOperationsService: ListOperationsService
	) {
		super();
	}

	private initialize( defaultFilterData: IFilter ): void {
		this._defaultFilterData = defaultFilterData;
		this._id = defaultFilterData.id;
		this.setListFilterValue( defaultFilterData.values );
	}

	private setListFilterValue( filterDataValues: FilterValueType[] ) {
		this.listFilterValue = [ ...filterDataValues ?? [] ];
		this.emitFilterItemChange();
	}

	private get prepareListFilterDataForEmit(): IActiveFilterData {
		return {
			param: this.listFilterValue as FilterListType
		};
	}

	private emitFilterItemChange(): void {
		this.filterItemChange.emit( this.prepareListFilterDataForEmit );
	}

	private clearControlValue(): void {
		this.setListFilterValue( null );
	}

	private resetFilter( resetMode: ListOperationsResetModeEnum ): void {

		const resetData: IFilter = this.listOperationsService.helper.filterResetDataByResetMode(
			this.listOperationIdentifier,
			this._id,
			resetMode
		);

		this.setListFilterValue( resetData.values );
	}

	checkFilterItemActiveState( itemValue: FilterValueType ): boolean {
		return this.listFilterValue.includes( itemValue );
	}

	onListFilterChange( itemValue: FilterValueType ): void {
		/*
		 * re-assignment of array instead of direct mutation because otherwise
		 * TypeErrors might be thrown ('index is read-only', 'property is
		 * non-configurable and can't be deleted', ...)
		 */
		this.listFilterValue =
			this.listFilterValue.includes( itemValue )
			? this.listFilterValue = this.listFilterValue.filter( ( value: FilterValueType ) => value !== itemValue ) 	// check item
			: [ ...this.listFilterValue, itemValue ]; // un-check item

		this.emitFilterItemChange();
	}
}
